import styled from '@emotion/styled';
import { Form, Formik } from 'formik';
import { useState } from 'react';

import { Button, InlineAlert, TextInput } from '@travelnest/hui';

import { Stack } from '@/components/ui/Stack';
import { Text } from '@/components/ui/Text';
import { useSendDiscountEvent } from '@/hooks/tracking/discount';
import { formatDiscountAmount, getAppliedDiscount } from '@/lib/discounts';
import { spacing } from '@/lib/theme';
import type { DiscountApplicationState } from '@/lib/tracking/discount';
import { useI18n } from '@/locales';
import type { IDirectDiscount } from '@/types/content';

const Row = styled(Form)`
	display: flex;
	align-items: flex-end;
	gap: ${spacing.space20};

	/* HUI's input root is a label; let it shrink so the button never overflows,
	   and drop the room it keeps under the input so the button lines up with it. */
	> :first-child {
		flex: 1;
		min-width: 0;
		margin-bottom: 0;
		padding-bottom: 0;
	}
	/* As tall as HUI's text field (44px box plus its 1px border). */
	> button {
		flex-shrink: 0;
		min-height: 46px;
	}
`;

interface DiscountCodeFormProps {
	appliedDiscount: IDirectDiscount | null | undefined;
	availableDiscounts: IDirectDiscount[];
	currency: string;
	onApply: (discount: IDirectDiscount) => void;
}

/**
 * Lets the guest enter a discount code at checkout. A wrong code shows an
 * error but never removes a discount that is already applied: the only way
 * out of a discount is a new, valid one.
 */
export const DiscountCodeForm = ({
	appliedDiscount,
	availableDiscounts,
	currency,
	onApply,
}: DiscountCodeFormProps) => {
	const t = useI18n();
	const [error, setError] = useState('');
	const [attempt, setAttempt] = useState<DiscountApplicationState>(
		appliedDiscount ? 'APPLIED' : 'NOT_PROVIDED',
	);

	useSendDiscountEvent(attempt, 'WIDGET');

	return (
		<Stack gap="space20">
			{appliedDiscount && (
				<InlineAlert
					variant="positive"
					heading={appliedDiscount.discountCode}
					content={t('booking.discount.applied', {
						amount:
							formatDiscountAmount(appliedDiscount, currency) ??
							'',
					})}
				/>
			)}
			<Formik
				initialValues={{
					discountCode: appliedDiscount?.discountCode ?? '',
				}}
				onSubmit={(values, { setSubmitting }) => {
					const code = values.discountCode.trim();
					const match = getAppliedDiscount(code, availableDiscounts);
					if (code === '') {
						setError(t('booking.discount.enterCode'));
						setAttempt('NOT_PROVIDED');
					} else if (!match) {
						setError(t('booking.discount.invalidCode'));
						setAttempt('UNRECOGNISED');
					} else {
						setError('');
						setAttempt('APPLIED');
						onApply(match);
					}
					setSubmitting(false);
				}}
			>
				{({ isSubmitting }) => (
					<Row noValidate>
						<TextInput
							name="discountCode"
							label={t('booking.discount.title')}
							placeholder={t('booking.discount.placeholder')}
							autoComplete="off"
						/>
						<Button
							type="submit"
							text={t('booking.discount.apply')}
							variant="primary"
							fill="light"
							isSubmitting={isSubmitting}
						/>
					</Row>
				)}
			</Formik>
			{error && (
				<Text variant="bodySmall" color="error" role="alert">
					{error}
				</Text>
			)}
		</Stack>
	);
};

interface DiscountAlertProps {
	/** `null` means a code was given in the URL but did not match an active discount. */
	discount: IDirectDiscount | null;
	currency: string;
	exceedsTotal?: boolean;
}

/** Tells the guest what a discount code from the link is worth, or why it was not applied. */
export const DiscountAlert = ({
	discount,
	currency,
	exceedsTotal,
}: DiscountAlertProps) => {
	const t = useI18n();
	if (discount === null) {
		return (
			<InlineAlert
				variant="negative"
				heading={t('booking.discount.notApplied')}
				content={t('booking.discount.expiredOrInvalid')}
			/>
		);
	}
	if (exceedsTotal) {
		return (
			<InlineAlert
				variant="negative"
				heading={t('booking.discount.notApplied')}
				content={t('booking.discount.exceedsTotal')}
			/>
		);
	}
	return (
		<InlineAlert
			variant="informative"
			content={t('booking.discount.willApply', {
				amount: formatDiscountAmount(discount, currency) ?? '',
			})}
		/>
	);
};
